import type { UseQueryResult } from '@tanstack/react-query'
import type { GroupStatus, PersonalDataFormInput } from './types'
import type { BookingDraft, PhoneNumber } from '@/scopes/checkout/domain/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import * as React from 'react'
import { useCheckoutContext } from '@/scopes/checkout/domain/CheckoutContext'
import { buildUrlWithCurrentParams } from '@/scopes/checkout/domain/helpers/BuildUrlWithCurrentParams'
import { useBookingDraftQueryKey, useCheckoutSid, useCheckoutSidSession } from '@/scopes/checkout/domain/hooks'
import { useGroupsDependency } from '@/scopes/checkout/groups/dependencies/context'

export function useBookingNumber() {
  const searchParams = useSearchParams()
  return React.useMemo(() => searchParams.get('bookingNumber') ?? '', [searchParams])
}

export function useTransformBookingToCheckoutQuery() {
  const { contextItemId, draftId: draftIdFromContext } = useCheckoutContext()
  const sid = useCheckoutSid()
  const queryClient = useQueryClient()
  const checkoutAPIClient = useGroupsDependency('checkoutAPIClient')
  const { data: groupData } = useGetGroupStatus()
  const masterBookingNumber = useBookingNumber()

  let shouldExecuteTransformBooking = false
  if (sid || draftIdFromContext) {
    shouldExecuteTransformBooking = !sid && !draftIdFromContext
  }
  else if (groupData?.childBookingNumber && groupData?.isValid) {
    shouldExecuteTransformBooking = true
  }

  const { data, isLoading, isFetching, isSuccess, isFetched, isError } = useQuery<BookingDraft>({
    queryKey: ['group-transform-booking-to-checkout', masterBookingNumber],
    queryFn: async () => checkoutAPIClient.transformBookingToCheckoutAsPost({
      bookingNumber: groupData?.childBookingNumber ?? 0,
      groupUserId: groupData?.bookingCustomerId ?? 0,
      contextItemId,
    }, groupData),
    enabled: shouldExecuteTransformBooking,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const { setDraftId } = useCheckoutSidSession()

  const invalidateBookingDraftQuery = React.useCallback((bookingDraftId: string) => {
    if (!bookingDraftId)
      return
    queryClient.invalidateQueries({ queryKey: ['booking-draft', bookingDraftId] })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we don't want to run this effect on every render
  }, [])

  React.useEffect(() => {
    if (!data || isError)
      return
    const draftId = data?.draftId
    if (draftId) {
      setDraftId && setDraftId(draftId)
      invalidateBookingDraftQuery(draftId)
    }
  }, [data?.draftId, setDraftId, invalidateBookingDraftQuery])

  return {
    isFetching,
    isLoading,
    isFetched,
    isSuccess,
    isError,
    data,
  }
}

export function useGetGroupStatus() {
  const masterBookingNumber = useBookingNumber()
  const groupBookingsAPIClient = useGroupsDependency('groupBookingsAPIClient')
  let queryEnabled = false
  if (masterBookingNumber) {
    queryEnabled = true
  }
  const { isLoading, data, isFetched } = useQuery<GroupStatus>({
    queryKey: ['group-booking-info', masterBookingNumber],
    queryFn: () => groupBookingsAPIClient.getGroupStatus(masterBookingNumber),
    enabled: queryEnabled,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    retry: false,
  })

  return {
    isLoading,
    data,
    isFetched,
  }
}

export function useSetParticipantGroupForm({ data: bookingDraft }: UseQueryResult<BookingDraft>) {
  const checkoutAPIClient = useGroupsDependency('checkoutAPIClient')
  const router = useGroupsDependency('router')
  const queryClient = useQueryClient()
  const sid = useCheckoutSid()
  const { contextItemId } = useCheckoutContext()
  const queryKey = useBookingDraftQueryKey()

  const setParticipantsFormSelection = async (formData: PersonalDataFormInput) => {
    if (!bookingDraft) {
      return
    }
    const participant = Object.values(bookingDraft?.participants)[0]
    const payload = [
      {
        ...formData,
        id: participant.id || '1',
        mobilePhoneNumber: mapPhoneNumberToString(formData.mobilePhoneNumber),
        emergencyPhoneNumber: mapPhoneNumberToString(formData.emergencyPhoneNumber),
      },
    ]
    const response = await checkoutAPIClient.setGroupParticipantsInformation({ sid, contextItemId }, payload)

    if (!response.ok) {
      return
    }

    const nextStep = bookingDraft?.navigation?.nextStep

    await queryClient.refetchQueries({ queryKey })

    if (!nextStep) {
      return
    }

    router.push(buildUrlWithCurrentParams(nextStep.url))
  }

  return {
    participants: bookingDraft?.participants ?? null,
    setParticipantsForm: setParticipantsFormSelection,
  }
}

function mapPhoneNumberToString(phoneNumber: PhoneNumber | null): string | null {
  if (!phoneNumber) {
    return null
  }

  return `+${phoneNumber.countryCode}${phoneNumber.phoneNumber}`
}

export function useSubscribeParticipantQueryKey() {
  const sid = useCheckoutSid()

  return React.useMemo(() => ['booking-draft-subscribe-participant', sid], [sid])
}

export function useSubscribeParticipant() {
  const groupBookingsAPIClient = useGroupsDependency('groupBookingsAPIClient')
  const sid = useCheckoutSid()
  const queryKey = useSubscribeParticipantQueryKey()
  const queryClient = useQueryClient()
  const { contextItemId } = useCheckoutContext()
  const [isLoading, setIsLoading] = React.useState(true)
  const [data, setData] = React.useState({} as GroupStatus)
  const [error, setError] = React.useState(false)
  const callTrackerRef = React.useRef<string | undefined>(undefined)

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        if (!sid) {
          return
        }
        const callKey = `${sid}-${contextItemId}`
        if (callTrackerRef.current === callKey) {
          return
        }
        callTrackerRef.current = callKey

        const response = await groupBookingsAPIClient.subscribeParticipant({ sid, contextItemId })
        if (response) {
          setData(response)
          queryClient.setQueryData(queryKey, response)
          sessionStorage.setItem('draftId_isConfirmed', 'true')
        }
        else {
          setError(true)
        }
      }
      catch {
        setError(true)
      }
      finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [sid, contextItemId, groupBookingsAPIClient, queryClient, queryKey])

  return {
    isLoading,
    data,
    error,
  }
}
